import React, { ButtonHTMLAttributes, forwardRef, FunctionComponent } from "react";
import { useDispatch } from "react-redux";
import { IconButton, ThemeProvider } from "@hxnova/react-components";
import { NovaTheme } from "@hxnova/themes";

import NavBarRedesignIconSlot from "./nav_bar_redesign_icon_slot";
import Tooltipable from "../../shared/tooltipable";
import resolveNavRoute, { NavRouteContext } from "./nav_bar_redesign_route";
import { RedesignNavItem } from "./nav_bar_redesign_options";

type NovaNavIconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active: boolean
  label: string
  icon: React.ReactNode
};

// Mirrors the NavBarManageUsersIcon pattern: a Nova IconButton wrapped in its own ThemeProvider,
// forwarding the ref so Tooltipable can anchor the hover/focus label that identifies the
// destination.
const NovaNavIconButton = forwardRef<HTMLButtonElement, NovaNavIconButtonProps>(
  ({ active, label, icon, className, ...rest }, ref) => (
    <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
      <IconButton
        ref={ref}
        variant="neutral"
        aria-label={label}
        aria-pressed={active}
        className={`NavBarRedesign-iconButton${className ? ` ${className}` : ""}${active ? " Nova-selected" : ""}`}
        {...rest}
      >
        {icon}
      </IconButton>
    </ThemeProvider>
  )
);
NovaNavIconButton.displayName = "NovaNavIconButton";

export type Props = {
  item: RedesignNavItem
  active: boolean
  routeContext: NavRouteContext
};

const NavBarRedesignIconButton: FunctionComponent<Props> = ({ item, active, routeContext }) => {
  const dispatch = useDispatch();
  const target = resolveNavRoute(item.base, routeContext);

  return <NavBarRedesignIconSlot selected={active}>
    <Tooltipable title={item.label}>
      <NovaNavIconButton
        active={active}
        label={item.label}
        icon={item.renderIcon(active)}
        onClick={() => { if (target) { dispatch(target); } }}
      />
    </Tooltipable>
  </NavBarRedesignIconSlot>;
};

export default NavBarRedesignIconButton;
